import { ArrowUpRight, Box, Heart } from "lucide-react";
import { Link } from "react-router";
import type { CommunityNote } from "../../../api/community";
import { MediaImage } from "../../../components/MediaImage";
import { Skeleton } from "../../../components/Skeleton";
import { usePinnedTrack } from "../../../hooks/usePinnedTrack";
import { authorInitial, excerpt, formatCount, thumbForNote } from "../../community/community-data";
import { noteKind } from "../home-data";

type HomeFeaturedProps = {
  notes: CommunityNote[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
};

export function HomeFeatured({ notes, loading, error, onRetry }: HomeFeaturedProps) {
  const showTrack = loading || notes.length > 0;
  const track = usePinnedTrack<HTMLElement, HTMLDivElement>(loading ? -1 : notes.length, 1.6);

  return (
    <section
      ref={track.sectionRef}
      className={`home-featured${track.pinned && showTrack ? " is-pinned" : ""}`}
      id="featured"
      aria-labelledby="home-featured-title"
      aria-busy={loading}
    >
      <div className="home-featured-stage">
        {showTrack ? (
          <div ref={track.trackRef} className="home-featured-track">
            <header className="home-featured-intro">
              <h2 id="home-featured-title">别人的材质，<br />怎样变成作品</h2>
              <p>社区里被推荐最多的情绪板与场景。点开任意一张，都能看到用了哪些材质。</p>
              <Link className="home-text-link" to="/community">进入社区 <ArrowUpRight size={16} /></Link>
            </header>
            {loading
              ? Array.from({ length: 3 }, (_, index) => (
                  <div className="home-work" key={index} aria-hidden="true"><Skeleton className="is-fill" /></div>
                ))
              : notes.map((note, index) => <HomeWorkCard note={note} index={index} key={note.id} />)}
            <Link className="home-featured-more" to="/community">
              <span>看更多社区作品</span>
              <ArrowUpRight size={34} strokeWidth={1.4} />
            </Link>
          </div>
        ) : (
          <div className="home-featured-empty">
            <h2 id="home-featured-title">社区作品</h2>
            <div className={error ? "home-status is-error" : "home-status"}>
              <p>{error ? "社区作品没有加载出来，请检查网络后重试。" : "社区里还没有可展示的作品，发布第一张情绪板吧。"}</p>
              {error ? <button type="button" onClick={onRetry}>重新加载</button> : <Link to="/community">去社区发布</Link>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function HomeWorkCard({ note, index }: { note: CommunityNote; index: number }) {
  const cover = thumbForNote(note);
  return (
    <article className={`home-work${index % 2 ? " is-low" : ""}`}>
      <Link className="home-work-link" to={`/community/${note.id}`} aria-label={`查看作品：${note.title}`}>
        <span className="home-work-media">
          {cover ? <MediaImage src={cover} alt={note.title} /> : <span className="home-work-fallback" />}
        </span>
        <span className="home-work-copy">
          <span className="home-work-kind">
            {note.hasScene ? <Box size={13} /> : null}
            {noteKind(note)}
          </span>
          <strong>{note.title}</strong>
          <small>{excerpt(note.content, 46)}</small>
          <span className="home-work-meta">
            <span>
              {note.authorAvatarUrl ? <img src={note.authorAvatarUrl} alt="" width={22} height={22} loading="lazy" decoding="async" /> : <b>{authorInitial(note.authorName)}</b>}
              {note.authorName || "匿名设计师"}
            </span>
            <span><Heart size={13} /> {formatCount(note.likeCount || 0)}</span>
          </span>
        </span>
      </Link>
    </article>
  );
}
